import { useState, useMemo } from 'react'
import { DateTime } from 'luxon'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { Card, Text, Button, Icon, Separator } from '@/ui'
import { TypographyStyles } from '@/styles'
import { UploadDocumentType } from '@/constants/businessConstants'
import { UserDocument } from '@/features/user/your-practice/documents/lib/userDocuments.slice'
import { faCreditCard, faUpload } from '@fortawesome/pro-regular-svg-icons'
import { EditableStatementTable } from '../EditableStatementTable'

// TODO: Migrate to V2
import FileUploadModal from '@/componentsV1/FileUpload/FileUploadModal'

interface ImonthYear {
  monthYear: string
  monthYearLabel: string
}

interface IStatementAccount {
  [name: string]: {
    account: string
    accountId: number
    monthList: ImonthYear[]
  }
}

interface IAccount {
  name: string
  mask: string | null
  logo: string
  financial_institution_name: string | null
}

export const MissingStatementMultipleUploadCard = ({
  statementMonth,
  accountObject,
  statements,
}: {
  statementMonth: IStatementAccount
  accountObject: IAccount
  statements: UserDocument[]
}) => {
  const [uploadedStatements, setUploadedStatements] = useState<UserDocument[]>(
    []
  )
  const [openUpload, setOpenUpload] = useState(false)

  const { filteredStatements, monthList } = useMemo(() => {
    const monthList = Object.values(statementMonth).flatMap((account) =>
      account.monthList.map((month) =>
        DateTime.fromISO(month.monthYear).toFormat(
          DATE_FORMATS_LUXON.MONTH_YEAR_FULL
        )
      )
    )

    const accountIds = Object.values(statementMonth).flatMap(
      (account) => account.accountId
    )

    const filteredStatements = statements.filter((statement) =>
      statement.statement?.statementFinancialAccounts?.some((account) =>
        accountIds.includes(account.financialAccountId)
      )
    )

    return {
      monthList,
      filteredStatements,
    }
  }, [statementMonth, statements])

  const setUploadedFile = ({
    doc,
  }: {
    fileName?: string
    id?: number
    doc?: UserDocument
  }) => {
    const statements = uploadedStatements
    if (doc?.id) {
      statements.push(doc)
      setUploadedStatements(statements)
    }
  }

  return (
    <div className="flex flex-col items-start text-center w-full">
      {Object.entries(statementMonth).map(
        ([account, value]: [
          string,
          { account: string; accountId: number; monthList: ImonthYear[] },
        ]) => (
          <div
            key={account}
            className="flex flex-row items-center pt-5 gap-2 text-left"
          >
            <Icon icon={faCreditCard} color="text-neutral-500" size="xl" />
            <Text variant={TypographyStyles.H2}>{value.account}</Text>
          </div>
        )
      )}

      <Separator />

      <Card className="flex flex-col items-start justify-between text-left w-full bg-muted">
        <div className="flex flex-col gap-4 w-full sm:flex-row sm:justify-between sm:items-center sm:gap-0">
          <div className="flex flex-col items-start justify-start min-w-0">
            <Text variant={TypographyStyles.H2}>
              {accountObject.financial_institution_name} statements
            </Text>
            <Text variant={TypographyStyles.BODY_SM}>
              Upload all {accountObject.financial_institution_name} statement(s)
              for the accounts above, covering the months listed here:
            </Text>
            <ul className="list-disc pl-5 pt-4 space-y-1">
              {monthList.map((value: string) => (
                <li key={`${value}-list`}>
                  <Text variant={TypographyStyles.BODY_SM}>{value}</Text>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex flex-col justify-center items-center w-full sm:w-[200px]">
            <Button
              onClick={() => setOpenUpload(true)}
              variant="secondary"
              className="flex items-center w-full sm:w-[150px]"
            >
              <Icon icon={faUpload} size="1x" className="mr-1" />
              Upload
            </Button>
          </div>
        </div>

        <FileUploadModal
          documentType={UploadDocumentType.STATEMENT}
          open={openUpload}
          close={() => setOpenUpload(false)}
          userFacing
          monthYear={undefined}
          setUploadedFile={setUploadedFile}
        />
      </Card>

      {Boolean(filteredStatements.length) && (
        <div className="flex flex-col items-start justify-between pt-2 text-center w-full">
          <EditableStatementTable documents={filteredStatements} />
        </div>
      )}
    </div>
  )
}
